import { useEffect, useState } from 'react';
import { Button, Center, Chip, Group, Input, Loader, NumberInput, Stack, Text } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { IconClockHour4, IconDeviceFloppy } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { SectionCard } from './SectionCard';
import { monthToDate, ym, sanaLocale, OY_FORMATI } from '../lib/month';

/** `payrollWorkDays` — maosh ish kunlari, ISO hafta kunlari vergul bilan ("1,2,3,4,5", spec 2026-10-21 §4.1). */
interface Qoida { fromMonth: string | null; graceMinutes: number; payrollWorkDays?: string }
const MAOSH_KUNLARI_SUKUT = '1,2,3,4,5';
/** "5,1,3" → "1,3,5" — server ham, `ozgarmagan` solishtiruvi ham tartiblangan qatorni kutadi. */
const kunlarQatori = (k: string[]) => [...k].sort((a, b) => Number(a) - Number(b)).join(',');
export const PAYROLL_RULE_KEY = ['payroll-rule'];

/**
 * MAOSH QOIDASI — ish vaqti bo'yicha hisob (spec 2026-10-14).
 *
 * "Qaysi oydan" bo'sh — qoida o'chiq (eski hisob). To'ldirilsa shu oydan boshlab oylik va
 * soatbay maosh belgilangan ish vaqti ichidagi vaqt bo'yicha hisoblanadi; "kechirim" dan
 * oshmagan kechikish/erta ketish kechiriladi. "Maosh ish kunlari" — oylik maoshning kunlik qiymati
 * shu hafta kunlari bo'yicha (dars kunlari `WorkDaysSettings` dan alohida). Backend:
 * `PUT /tenant/payroll-rule` (STAFF).
 */
export function PayrollRuleSettings() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: PAYROLL_RULE_KEY,
    queryFn: async () => (await api.get<Qoida>('/tenant/payroll-rule')).data,
  });
  const [oy, setOy] = useState('');
  // Server faqat butun SON qabul qiladi; bo'sh maydon ('') saqlashni bloklaydi
  const [kechirim, setKechirim] = useState<number | ''>(5);
  const [kunlar, setKunlar] = useState<string[]>(MAOSH_KUNLARI_SUKUT.split(','));
  // Serverdan kelgan qiymat bir marta qo'yiladi (foydalanuvchi tanlovi ustiga yozilmasin)
  const [filled, setFilled] = useState(false);
  useEffect(() => {
    if (filled || !data) return;
    setOy(data.fromMonth ?? '');
    setKechirim(data.graceMinutes);
    setKunlar((data.payrollWorkDays ?? MAOSH_KUNLARI_SUKUT).split(',').filter(Boolean));
    setFilled(true);
  }, [data, filled]);

  const save = useMutation({
    mutationFn: async (grace: number) =>
      (await api.put<Qoida>('/tenant/payroll-rule', { fromMonth: oy || null, graceMinutes: grace, payrollWorkDays: kunlarQatori(kunlar) })).data,
    onSuccess: (r) => {
      qc.setQueryData(PAYROLL_RULE_KEY, r);
      // Maosh ko'rinadigan joylar yangi qoida bilan qayta hisoblanadi
      for (const key of ['salary-compute', 'payroll-summary', 'payroll-oylik']) qc.invalidateQueries({ queryKey: [key] });
      notifications.show({ color: 'green', message: r.fromMonth ? t('payrollRule.saved') : t('payrollRule.off') });
    },
    onError: (e: unknown) =>
      notifications.show({
        color: 'red',
        message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.error'),
      }),
  });

  const ozgarmagan = !!data && oy === (data.fromMonth ?? '') && kechirim === data.graceMinutes
    && kunlarQatori(kunlar) === kunlarQatori((data.payrollWorkDays ?? MAOSH_KUNLARI_SUKUT).split(',').filter(Boolean));
  // Kechirim bo'sh yoki birorta ham maosh kuni tanlanmagan — saqlab bo'lmaydi (server ham rad etadi)
  const bosh = typeof kechirim !== 'number' || kunlar.length === 0;

  return (
    <SectionCard title={t('payrollRule.section')} subtitle={t('payrollRule.sectionDesc')} icon={IconClockHour4} maw={520} mb="md">
      {isLoading ? (
        <Center py="md"><Loader size="sm" /></Center>
      ) : (
        <Stack gap="sm">
          <MonthPickerInput
            label={t('payrollRule.fromMonth')}
            description={t('payrollRule.fromMonthHint')}
            placeholder={t('payrollRule.offPlaceholder')}
            value={monthToDate(oy)}
            onChange={(d) => setOy(d ? ym(d) : '')}
            locale={sanaLocale(i18n.language)}
            valueFormat={OY_FORMATI}
            clearable
          />
          <NumberInput
            label={t('payrollRule.grace')}
            description={t('payrollRule.graceHint')}
            min={0}
            max={120}
            step={1}
            allowDecimal={false}
            allowNegative={false}
            clampBehavior="strict"
            suffix={` ${t('payrollRule.min')}`}
            value={kechirim}
            error={typeof kechirim !== 'number'}
            onChange={(v) => setKechirim(typeof v === 'number' ? v : '')}
          />
          <Input.Wrapper
            label={t('payrollRule.workDays')}
            description={t('payrollRule.workDaysHint')}
            error={kunlar.length === 0 ? t('payrollRule.workDaysRequired') : undefined}
          >
            <Chip.Group multiple value={kunlar} onChange={setKunlar}>
              <Group gap={6} mt={6}>
                {['1', '2', '3', '4', '5', '6', '7'].map((d) => (
                  <Chip key={d} value={d} size="sm">{t(`days.${d}`)}</Chip>
                ))}
              </Group>
            </Chip.Group>
          </Input.Wrapper>
          {!oy && <Text size="xs" c="dimmed">{t('payrollRule.offHint')}</Text>}
          <Group justify="flex-end">
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              loading={save.isPending}
              disabled={ozgarmagan || bosh}
              onClick={() => typeof kechirim === 'number' && save.mutate(kechirim)}
            >
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      )}
    </SectionCard>
  );
}
